// OpenIssueCountBadge — "⚠ N open issues" pill rendered alongside a
// deliverable / milestone / KPI / KPI-period to surface that issues link
// back to it. Clicking the pill navigates to the Issues tab with a
// pre-applied "Linked to" filter so the user lands on exactly the matched
// rows.
//
// The count is derived from the contract's issue list (small per contract)
// — no extra GSI or back-link index. Issues whose `status === "closed"` are
// excluded; archived issues are excluded by the list query default. Renders
// nothing when the count is zero so the badge never adds visual noise to an
// unrelated row.

import { Link } from "react-router";
import { AlertTriangle } from "lucide-react";
import { useAllIssuesForBackLinkCount } from "../../../../hooks/useTracking";
import type { IssueLinkTargetType } from "../../../../types";

export interface OpenIssueCountBadgeProps {
  contractId: string;
  targetType: IssueLinkTargetType;
  targetId: string;
}

export default function OpenIssueCountBadge({
  contractId,
  targetType,
  targetId,
}: OpenIssueCountBadgeProps) {
  // Paginates through the full contract-wide issue list — a single-page
  // query would silently under-count on busy contracts where issues mix
  // with other tracking rows past the GSI1 page boundary.
  const { data: issues } = useAllIssuesForBackLinkCount(contractId);

  // Composite-keyed filter: same id under a different targetType doesn't
  // bleed into the count (e.g. a deliverable d-1 and a milestone d-1 are
  // distinct link targets).
  const openCount = issues.reduce((n, issue) => {
    if (issue.status === "closed") return n;
    const matched = (issue.links ?? []).some(
      (l) => l.targetType === targetType && l.targetId === targetId,
    );
    return matched ? n + 1 : n;
  }, 0);

  if (openCount === 0) return null;

  // Deep-link shape consumed by IssueTab. `?tab=issue&linkedType=…&linkedId=…`
  // is distinct from `?item=` — the latter opens a specific issue, the former
  // pre-applies the "Linked to" filter. Both can coexist.
  const target = `?tab=issue&linkedType=${encodeURIComponent(targetType)}&linkedId=${encodeURIComponent(targetId)}`;

  const label = `${openCount} open ${openCount === 1 ? "issue" : "issues"}`;

  return (
    <Link
      to={target}
      data-testid="open-issue-count-badge"
      aria-label={`${label} linked here — view`}
      className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-caption font-medium text-red-700 hover:bg-red-100 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300 dark:hover:bg-red-950/60"
    >
      <AlertTriangle aria-hidden="true" className="h-3 w-3" />
      <span>{label}</span>
    </Link>
  );
}
